import { FC, useMemo, useState } from 'react'

import { UserDisplay } from '@/components'
import { Eye } from '@/components/Icons/General/Eye'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
} from '@/components/ui'
import { Button } from '@/components/ui/button'
import { useDocumentViewAudit } from '@/features/audit/hooks'
import { useDocumentStore } from '@/features/document'
import { useCurrentUser } from '@/hooks/useAuth'
import { User } from '@/schema'
import { cn, formatFullName } from '@/utils/common'

export interface ViewerTrackerProps {
  documentId: string
}

const ViewerListItem = ({ viewer }: { viewer: User }) => {
  return (
    <div className="flex items-center justify-between p-2 transition-colors rounded-xl hover:bg-gray-50">
      <UserDisplay user={viewer} avatarSize="size-10" />
    </div>
  )
}

export const ViewerTracker: FC<ViewerTrackerProps> = ({ documentId }) => {
  const [open, setOpen] = useState(false)
  const { versionIdByDocumentId } = useDocumentStore()
  const versionId = versionIdByDocumentId[documentId]
  const { data: currentUser } = useCurrentUser()

  const {
    data: auditData,
    isLoading,
    isFetching,
  } = useDocumentViewAudit(documentId, versionId || '')

  const isVersionPending = !versionId
  const isAuditLoading = isLoading || isFetching
  const loading = isVersionPending || isAuditLoading

  const viewers = useMemo(() => {
    const apiViewers = auditData?.data ?? []

    if (!currentUser) {
      return apiViewers
    }

    if (isVersionPending || isAuditLoading) {
      return [
        {
          id: currentUser.id,
          email: currentUser.email,
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
          avatar_url: null,
          roles: currentUser.roles,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ]
    }

    // Check if current user is already in API response
    const hasCurrentUser = apiViewers.some(
      (viewer) => viewer.id === currentUser.id
    )

    if (hasCurrentUser) {
      return apiViewers
    }

    return [
      {
        id: currentUser.id,
        email: currentUser.email,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        avatar_url: null,
        roles: currentUser.roles,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      ...apiViewers,
    ]
  }, [auditData?.data, currentUser, isVersionPending, isAuditLoading])

  const sortedViewers = useMemo(() => {
    return [...viewers].sort((a, b) => {
      const nameA = formatFullName(a.first_name, a.last_name).toLowerCase()
      const nameB = formatFullName(b.first_name, b.last_name).toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [viewers])

  const viewCount = viewers.length

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outlined"
          color="default"
          className={cn(
            'rounded-full gap-sm pl-sm pr-md py-xxs border border-solid border-gray-light-300 hover:border-gray-500 cursor-pointer text-xs text-gray-700',
            loading && 'opacity-75'
          )}
          aria-label={`${viewCount} ${viewCount === 1 ? 'viewer' : 'viewers'}`}
          leadingIcon={<Eye className="text-gray-700" />}
          disabled={loading}
        >
          <span className="flex items-center gap-1 py-1">
            {loading ? (
              <>
                <Spinner className="text-gray-700 size-3" />
                <span className="sr-only">Loading viewers...</span>
              </>
            ) : (
              viewCount
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 px-2 py-1" align="start" sideOffset={10}>
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-secondary-900">
            {viewCount === 1 ? 'Viewer (1)' : `Viewers (${viewCount})`}
          </h3>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-6 text-brand-600" />
            </div>
          )}
          {!loading && viewers.length === 0 && (
            <div className="py-4 text-sm text-center text-secondary-500">
              No viewers yet
            </div>
          )}
          {!loading && viewers.length > 0 && (
            <div className="overflow-y-auto max-h-80">
              {sortedViewers.map((viewer) => (
                <ViewerListItem key={viewer.id} viewer={viewer} />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
